import OwaWorkload from 'owa-workloads/lib/store/schema/OwaWorkload';
import { clearSessionStorage, getItemFromSessionStorage } from 'mini-session-storage';
import { SessionData } from 'owa-service/lib/types/SessionData';
import { USE_STARTUP_DATA_FROM_EXTENSION } from 'extension-communication/lib/constants/useDataFromExtension';

const HOURS_LIMIT_FOR_USING_SESSION_STORAGE = 2;
const MILLISECONDS_IN_ONE_HOUR = 3600000;
/**
 * If session data is present in session storage, we resolve the bootstrap promises immediately so as to start the bootstrap process immediately
 * If session data is not present, we send in the original bootstrapConfig promises and wait for it to get resolved.
 * @param bootstrapConfig Original bootstrap configuration
 */

export interface BootstrapConfigExtended {
    /**
     * Original bootstrap Config
     */
    sessionDataPromise: Promise<SessionData>;
    /**
     * Required to avoid re bootstrapping when the original network session data comes in
     */
    hasDataInSessionStorage: boolean;
}

/**
 * Function to resolve sessiondata promises if found in SessionStorage otherwise return the unresolved sessiondata promises
 * @param bootstrapConfig Original bootstrap config
 * @param workload Determines the workload i.e. Mail/Calendar/People
 */
export function getBootstrapConfig(
    bootstrapConfig: Promise<SessionData>,
    workload: OwaWorkload
): BootstrapConfigExtended {
    if (!USE_STARTUP_DATA_FROM_EXTENSION) {
        const isWorkLoadMail = workload === OwaWorkload.Mail;

        if (isWorkLoadMail) {
            return {
                sessionDataPromise: bootstrapConfig,
                hasDataInSessionStorage: false,
            };
        }
    }

    const sessionData = getItemFromSessionStorage('session');
    const lastSavedTime = getItemFromSessionStorage('lastSavedTime');

    let isDataStale = false;

    if (
        lastSavedTime &&
        Math.floor((Date.now() - lastSavedTime) / MILLISECONDS_IN_ONE_HOUR) >
            HOURS_LIMIT_FOR_USING_SESSION_STORAGE
    ) {
        isDataStale = true;
        clearSessionStorage();
    }

    if (!sessionData || isDataStale) {
        return {
            sessionDataPromise: bootstrapConfig,
            hasDataInSessionStorage: false,
        };
    }

    // We will also read from findFolder and calendarFolders keys and make a new sessiondata
    // object which contains old sessiondata and the findFolder calendarfolders data
    if (workload === OwaWorkload.Calendar) {
        sessionData.getCalendarFolders = getItemFromSessionStorage('getCalendarFolders');
    } else if (workload === OwaWorkload.Mail) {
        sessionData.findFolders = getItemFromSessionStorage('findFolders');
        sessionData.findConversation = getItemFromSessionStorage('findConversation');
        sessionData.findMailFolderItem = getItemFromSessionStorage('findMailFolderItem');
    }

    return {
        sessionDataPromise: sessionData ? Promise.resolve(sessionData) : bootstrapConfig,
        hasDataInSessionStorage: true,
    };
}
